import {
  AUTOMATION_TRIGGER_OPTIONS,
  AUTOMATION_ACTION_OPTIONS,
} from "@/lib/const";
import { flattenActions } from "@/lib/flattenActions";

// Chaining metadata for one template. Triggers/actions form parent→child
// hierarchies in the catalog (conditionalTriggers / subsequentActions) that
// `flattenTriggers`/`flattenActions` discard. Both the prompt generator (to
// annotate the catalog) and the import dialog (to warn about broken chains)
// need the relationship back — neither the parse schema nor
// `validateAutomationForImport` catches an orphan child or a parent missing a
// REQUIRED child.
export interface ChainInfo {
  parentId?: string;
  parentLabel?: string;
  /** This template is a REQUIRED child of its parent. */
  required?: boolean;
  children?: { id: string; label: string; required: boolean }[];
}

// Walk the raw (non-flattened) catalog and record, per template id, its parent
// and its direct children. `isRequired` is catalog-specific: actions carry a
// `required` flag on mandatory sub-actions; triggers have no such concept
// (conditions are always optional refinements), so their accessor is () => false.
export const buildChainMap = <
  T extends { id: string; buttonOptions: { text: string } },
>(
  roots: T[],
  getChildren: (n: T) => T[] | undefined,
  isRequired: (n: T) => boolean
): Map<string, ChainInfo> => {
  const map = new Map<string, ChainInfo>();
  const walk = (nodes: T[], parent?: T) => {
    for (const node of nodes) {
      const info: ChainInfo = map.get(node.id) ?? {};
      if (parent) {
        info.parentId = parent.id;
        info.parentLabel = parent.buttonOptions.text;
        info.required = isRequired(node);
      }
      const children = getChildren(node) ?? [];
      if (children.length) {
        info.children = children.map((child) => ({
          id: child.id,
          label: child.buttonOptions.text,
          required: isRequired(child),
        }));
      }
      map.set(node.id, info);
      if (children.length) walk(children, node);
    }
  };
  walk(roots);
  return map;
};

export const TRIGGER_CHAIN = buildChainMap(
  AUTOMATION_TRIGGER_OPTIONS,
  (t) => t.conditionalTriggers,
  () => false
);

export const ACTION_CHAIN = buildChainMap(
  AUTOMATION_ACTION_OPTIONS,
  (a) => a.subsequentActions,
  (a) => !!a.required
);

const ACTION_LABELS = new Map(
  flattenActions(AUTOMATION_ACTION_OPTIONS).map((a) => [
    a.id,
    a.buttonOptions.text,
  ])
);

const actionLabel = (id: string): string => ACTION_LABELS.get(id) ?? id;

export interface ChainingIssue {
  automationName: string;
  message: string;
}

// The minimal shape a chaining check needs — satisfied by both a parsed export
// file's automations and live `Automation` objects.
interface ChainCheckable {
  name?: string;
  actions: { templateId: string }[];
}

// Flags action chains the import pipeline accepts but that won't behave: a
// sub-action present without its parent (does nothing on its own), or a parent
// present without a REQUIRED sub-action (e.g. "Flash color" with no color).
//
// Only ACTIONS are checked. Trigger conditions have no required/parent
// contract — a lone condition (e.g. "container is X") is a valid standalone
// state trigger — so linting them would produce false positives.
export function findChainingIssues(
  automations: ChainCheckable[]
): ChainingIssue[] {
  const issues: ChainingIssue[] = [];
  automations.forEach((automation) => {
    const name = automation.name || "Unnamed automation";
    const presentIds = new Set(automation.actions.map((a) => a.templateId));

    automation.actions.forEach((action) => {
      const info = ACTION_CHAIN.get(action.templateId);
      if (!info) return;

      if (info.parentId && !presentIds.has(info.parentId)) {
        issues.push({
          automationName: name,
          message: `"${actionLabel(action.templateId)}" only works together with "${info.parentLabel}", which this automation doesn't include.`,
        });
      }

      info.children?.forEach((child) => {
        if (child.required && !presentIds.has(child.id)) {
          issues.push({
            automationName: name,
            message: `"${actionLabel(action.templateId)}" is missing its required "${child.label}".`,
          });
        }
      });
    });
  });
  return issues;
}
